import classNames from "classnames";
import React from "react";
import { t } from "ttag";

import checkBadgeGreenCircle from "../../../../img/icons/check-badge-green-circle.png";
import circleEmpty from "../../../../img/icons/circle-empty.png";
import { FinancingPlan } from "../../../models/financing";
import format from "../../../utils/format";
import {
    PRIMARY_PAYMENT_METHOD_KEY,
    SECONDARY_PAYMENT_METHOD_KEY,
} from "../constants";
import { Dispatchers } from "../dispatchers";
import { IPayment } from "../reducers.interfaces";

const NAMES: { [c in IPayment["method_type"]]: string } = {
    "default": "",
    "cash": t`Cash`,
    "pay-later": t`Pay Later`,
    "cybersource": t`Credit Card`,
    "bluefin": t`Secure Credit Card`,
    "financing": t`Financing`,
};

interface IProps {
    primary: IPayment["method_type"];
    secondary: IPayment["method_type"]; // what secondary method is available
    selected: boolean;
    methodKey: string;
    grandTotal: string | null;
    plans: FinancingPlan[];
    addPaymentMethod: Dispatchers["addPaymentMethod"];
    removePaymentMethod: Dispatchers["removePaymentMethod"];
    onClick: (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ) => void;
}

export const PaymentEnabledField = (props: IProps): React.ReactNode => {
    const isPrimary = (): boolean => {
        return props.methodKey === PRIMARY_PAYMENT_METHOD_KEY;
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        if (props.methodKey === SECONDARY_PAYMENT_METHOD_KEY) {
            // adding secondary, so split pay
            const grandTotal: number = props.grandTotal
                ? parseFloat(props.grandTotal)
                : 0;
            let amount: number = Math.round((100 * grandTotal) / 2) / 100; // Round to hundreds, 50% split
            if (props.primary === "financing") {
                const plansAboveZero = props.plans
                    .map((plan) => parseFloat(plan.product_price_threshold))
                    .filter((a) => a > 0);
                const minFinancingAmount = plansAboveZero.length
                    ? Math.min(...plansAboveZero)
                    : 0;
                amount = Math.max(amount, minFinancingAmount);
            }
            const secondaryAmount: number = Math.max(0, grandTotal - amount);
            // set primary method based on what was clicked
            props.addPaymentMethod(
                PRIMARY_PAYMENT_METHOD_KEY,
                props.primary,
                format.decimal(amount),
            );
            // add the the secondary method
            props.addPaymentMethod(
                SECONDARY_PAYMENT_METHOD_KEY,
                props.secondary,
                format.decimal(secondaryAmount),
            );
        } else {
            // regular, non-split payment
            const amount = props.grandTotal;
            props.addPaymentMethod(
                PRIMARY_PAYMENT_METHOD_KEY,
                props.primary,
                format.decimal(amount),
            );
            // remove secondary method
            props.removePaymentMethod(SECONDARY_PAYMENT_METHOD_KEY);
        }
        props.onClick(event);
    };

    const classes = classNames({
        "checkout-step__payment-type": true,
        "checkout-step__payment-type--active": props.selected,
        "checkout-step__payment-type-secondary": !isPrimary(),
        [`checkout-step__payment-type--${props.primary}`]: true,
        "al-checkout-step__payment-type": true,
        [`al-checkout-step__payment-type--${props.primary}`]: true,
    });
    const inputID = isPrimary()
        ? `payment-enabled-field-${props.primary}`
        : `payment-enabled-field-${props.primary}-${props.secondary}`;

    return (
        <li onClick={handleClick} className={classes}>
            <label htmlFor={inputID}>
                <input
                    id={inputID}
                    type="radio"
                    checked={props.selected}
                    readOnly={true}
                    name="payment-method"
                    aria-describedby="payment-method-instructions"
                />
                {props.selected ? (
                    <img alt="" src={checkBadgeGreenCircle} />
                ) : (
                    <img alt="" src={circleEmpty} />
                )}
                {props.methodKey === PRIMARY_PAYMENT_METHOD_KEY
                    ? NAMES[props.primary]
                    : "Split Pay"}
                {props.methodKey === SECONDARY_PAYMENT_METHOD_KEY &&
                    props.primary === "cybersource" &&
                    " Two Credit Cards"}
                {props.methodKey === SECONDARY_PAYMENT_METHOD_KEY &&
                    props.primary === "bluefin" &&
                    " Two Credit Cards"}
                {props.methodKey === SECONDARY_PAYMENT_METHOD_KEY &&
                    props.primary === "financing" &&
                    " Financing and Credit Card"}
            </label>
        </li>
    );
};
